import { useState, useRef, useEffect } from "react";
import { createPortal } from 'react-dom';
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { UserSessionSelector } from 'store/UserSession';

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import TokenService from "config/token.service";
import { fetchUser } from "store/UserSession/features";
import SidebarWebsiteDropdown from 'components/AdminClient/SidebarWebsiteDropdown';
import {
  aiSmartContentTypeSelector,
  setAISmartContentType,
} from 'store/AdminClient/AISmartContentType';
import { CONSULTANT_PROJECT_ROUTES } from 'pages/consultant/projects/constants';

import '../../AdminClient/Sidebar.css';
import './SidebarConsultant.css';

const CONSULTANT_PROJECT_PATHS = [
  CONSULTANT_PROJECT_ROUTES.IN_PROGRESS,
  CONSULTANT_PROJECT_ROUTES.DELAYED,
  CONSULTANT_PROJECT_ROUTES.VALIDATED,
];

const CONSULTANT_IA_CONTENT_TYPES = [
  { type: 'OTHER', label: 'Contenu web', icon: '/Images/sidebar-icons/page_categ.png' },
  { type: 'PRODUCT', label: 'Fiche produit', icon: '/Images/sidebar-icons/fiche_produit.png' },
  { type: 'GMB', label: 'Fiche GMB', icon: '/Images/sidebar-icons/fiche_gmb.png' },
  { type: 'STORE_LOCATOR', label: 'Store locator', icon: '/Images/sidebar-icons/store_locator.png' },
  { type: 'LONG_CATEGORY', label: 'Page catégorie', icon: '/Images/sidebar-icons/page_categ.png' },
  { type: 'CONTENT_IMPROVEMENT', label: 'Enrichissement', icon: '/Images/sidebar-icons/enrichissement.png' },
];

const CONSULTANT_SOCIAL_MEDIA_TYPES = [
  { type: 'YOUTUBE', label: 'Youtube' },
];

function Item({ to, icon: Icon, label, active, onClick, right, className = "", ariaControls, ariaExpanded, style }) {
  const Comp = to ? Link : "button";
  const commonProps = {
    onClick,
    className: `sb-item ${active ? "active" : ""} ${className}`,
    "aria-current": active ? "page" : undefined,
    "aria-controls": ariaControls,
    "aria-expanded": ariaExpanded,
    style,
  };
  return (
    <Comp {...(to ? { to } : { type: "button" })} {...commonProps}>
      {Icon && <Icon className="sb-ic" aria-hidden="true" />}
      <span className="sb-label">{label}</span>
      {right}
    </Comp>
  );
}

export default function SidebarConsultant() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  let { user } = useSelector(UserSessionSelector);

  const is = (p) => pathname === p;
  const isProjectRoute = CONSULTANT_PROJECT_PATHS.includes(pathname);
  const isIARoute =
    pathname === CONSULTANT_PROJECT_ROUTES.IA_SMART ||
    pathname.startsWith(CONSULTANT_PROJECT_ROUTES.CREATE_IA_SMART_TEXT);
  const currentAISmartType = useSelector(aiSmartContentTypeSelector);

  const isAISmartType = (type) => {
    if (!isIARoute) return false;
    const current = currentAISmartType || 'PRODUCT';
    return current === type;
  };

  const [openProjects, setOpenProjects] = useState(true);
  const [openGenerator, setOpenGenerator] = useState(true);
  const [openSocial, setOpenSocial] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const userMenuDropdownRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const toggleProjects = () => {
    setOpenProjects((v) => !v);
    setOpenGenerator(false);
    setOpenSocial(false);
  };
  const toggleGenerator = () => {
    setOpenGenerator((v) => !v);
    setOpenProjects(false);
    setOpenSocial(false);
  };
  const toggleSocial = () => {
    setOpenSocial((v) => !v);
    setOpenProjects(false);
    setOpenGenerator(false);
  };
  const toggleSidebar = () => setIsCollapsed((v) => !v);

  useEffect(() => {
    if (isProjectRoute) {
      setOpenProjects(true);
    }
    if (isIARoute) {
      setOpenGenerator(true);
    }
  }, [pathname, isProjectRoute, isIARoute]);

  useEffect(() => {
    if (!openUserMenu || !userMenuRef.current) return undefined;

    const updateMenuPosition = () => {
      const triggerRect = userMenuRef.current.getBoundingClientRect();
      const dropdownHeight = userMenuDropdownRef.current?.offsetHeight ?? 48;
      setMenuPosition({
        top: triggerRect.bottom - dropdownHeight,
        left: triggerRect.right + 4,
      });
    };

    updateMenuPosition();
    const rafId = requestAnimationFrame(updateMenuPosition);

    return () => cancelAnimationFrame(rafId);
  }, [openUserMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openUserMenu) return;

      const dropdown = document.querySelector('[data-user-menu-dropdown]');
      const isClickInsideDropdown = dropdown && dropdown.contains(event.target);
      const isClickInsideButton = userMenuRef.current && userMenuRef.current.contains(event.target);

      if (!isClickInsideDropdown && !isClickInsideButton) {
        setOpenUserMenu(false);
      }
    };

    if (openUserMenu) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openUserMenu]);

  return (
    <aside
      className={`sb-root sb-root--consultant ${isCollapsed ? 'sb-root--collapsed' : ''}`}
      aria-label="Sidebar navigation"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: isCollapsed ? 'column' : 'row',
          padding: '0 6px 0 20px',
          height: '40px',
        }}
      >
        <Link
          to="/Consultant/MainMenu"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isCollapsed ? '0' : '110px',
          }}
        >
          <img
            className="logo-m"
            src="Images/new1-logo.png"
            alt="TYPIX logo"
            style={{ maxWidth: '80px', height: 'auto', display: 'block' }}
          />
        </Link>
        {!isCollapsed && (
          <img
            src="Images/icon-menu/icone-close-menu.png"
            alt="menu icon"
            onClick={toggleSidebar}
            style={{
              width: '35px',
              height: '33px',
              cursor: 'pointer',
              marginLeft: '90px',
            }}
          />
        )}
        {isCollapsed && (
          <img
            src="Images/icon-menu/icon-open-menu.png"
            alt="menu icon"
            onClick={toggleSidebar}
            style={{
              width: '35px',
              height: '33px',
              cursor: 'pointer',
              marginTop: '8px',
            }}
          />
        )}
      </div>

      <SidebarWebsiteDropdown
        isCollapsed={isCollapsed}
        canAddWebsite={false}
        skipCompanyQuotas
      />

      <div className="sb-section" style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0, padding: '0 14px', gap: '8px' }}>
        <div className="sb-section-title">
          ANALYSES
        </div>
        <nav className="sb-nav">
          <Item
            to="/Consultant/MainMenu"
            icon={() => <img src="/Images/sidebar-icons/dashboard.png" alt="Dashboard" className="sb-menu-item-icon" />}
            label={t("Tableau de bord")}
            active={is("/Consultant/MainMenu")}
            className="sb-menu-item"
          />
        </nav>

        <div className="sb-section-title">
          {t('Espace de travail AI')}
        </div>
        <nav className="sb-nav">
          <Item
            icon={() => <img src="/Images/sidebar-icons/redact_ai.png" alt="Rédaction IA" className="sb-menu-item-icon" />}
            label={t('Rédaction IA')}
            onClick={toggleGenerator}
            right={<ExpandMoreIcon className={`sb-caret ${openGenerator ? 'open' : ''}`} aria-hidden="true" />}
            ariaControls="submenu-consultant-generator"
            ariaExpanded={openGenerator}
            className="sb-menu-item sb-menu-item--has-submenu"
          />
          <div
            id="submenu-consultant-generator"
            className={`sb-subwrap ${openGenerator ? 'open' : ''}`}
            role="region"
            aria-hidden={!openGenerator}
          >
            <div className="sb-connector" />
            <div className="sb-sub">
              {CONSULTANT_IA_CONTENT_TYPES.map(({ type, label, icon }) => (
                <Item
                  key={type}
                  to={CONSULTANT_PROJECT_ROUTES.IA_SMART}
                  onClick={() => dispatch(setAISmartContentType(type))}
                  active={isAISmartType(type)}
                  label={t(label)}
                  className="sb-subitem"
                  icon={() => <img src={icon} alt={label} className="sb-menu-item-icon" />}
                />
              ))}
            </div>
          </div>

          <Item
            icon={() => <img src="/Images/sidebar-icons/social_media_ai.png" alt="Social Media IA" className="sb-menu-item-icon" />}
            label={t('Social Media IA')}
            onClick={toggleSocial}
            right={<ExpandMoreIcon className={`sb-caret ${openSocial ? 'open' : ''}`} aria-hidden="true" />}
            ariaControls="submenu-consultant-social"
            ariaExpanded={openSocial}
            className="sb-menu-item sb-menu-item--has-submenu"
          />
          <div
            id="submenu-consultant-social"
            className={`sb-subwrap ${openSocial ? 'open' : ''}`}
            role="region"
            aria-hidden={!openSocial}
          >
            <div className="sb-connector" />
            <div className="sb-sub">
              {CONSULTANT_SOCIAL_MEDIA_TYPES.map(({ type, label }) => (
                <Item
                  key={type}
                  to={CONSULTANT_PROJECT_ROUTES.IA_SMART}
                  onClick={() => dispatch(setAISmartContentType(type))}
                  active={isAISmartType(type)}
                  label={t(label)}
                  className="sb-subitem"
                />
              ))}
              <Item
                to="#"
                label={t('Tiktok')}
                className="sb-subitem sb-disabled"
                right={<LockOutlinedIcon className="sb-lock" aria-hidden="true" />}
              />
              <Item
                to="#"
                label={t('Linkedin')}
                className="sb-subitem sb-disabled"
                right={<LockOutlinedIcon className="sb-lock" aria-hidden="true" />}
              />
            </div>
          </div>

          <Item
            to={CONSULTANT_PROJECT_ROUTES.TRANSLATION_IA}
            icon={() => <img src="/Images/sidebar-icons/traduction.png" alt="Traduction IA" className="sb-menu-item-icon" />}
            label={t('Traduction IA')}
            active={
              is(CONSULTANT_PROJECT_ROUTES.TRANSLATION_IA) ||
              is(CONSULTANT_PROJECT_ROUTES.CREATE_TRANSLATION_IA)
            }
            className="sb-menu-item"
          />
        </nav>

        <div className="sb-section-title">
          PROJETS
        </div>
        <nav className="sb-nav">
          <Item
            icon={() => <img src="/Images/sidebar-icons/projet.png" alt="Projet" className="sb-menu-item-icon" />}
            label={t("Projets")}
            onClick={toggleProjects}
            right={<ExpandMoreIcon className={`sb-caret ${openProjects ? 'open' : ''}`} aria-hidden="true" />}
            ariaControls="submenu-projects"
            ariaExpanded={openProjects}
            className="sb-menu-item sb-menu-item--has-submenu"
          />
          <div id="submenu-projects" className={`sb-subwrap ${openProjects ? 'open' : ''}`} role="region" aria-hidden={!openProjects}>
            <div className="sb-connector" />
            <div className="sb-sub">
              <Item to={CONSULTANT_PROJECT_ROUTES.IN_PROGRESS} label={t("En cours")} active={is(CONSULTANT_PROJECT_ROUTES.IN_PROGRESS)} className="sb-subitem" />
              <Item to={CONSULTANT_PROJECT_ROUTES.DELAYED} label={t("En retard")} active={is(CONSULTANT_PROJECT_ROUTES.DELAYED)} className="sb-subitem" />
              <Item to={CONSULTANT_PROJECT_ROUTES.VALIDATED} label={t("Validés")} active={is(CONSULTANT_PROJECT_ROUTES.VALIDATED)} className="sb-subitem" />
            </div>
          </div>
        </nav>
      </div>

      <div className="sb-user-profile" style={{ position: 'relative', marginTop: 'auto', marginBottom: '0' }}>
        <div
          ref={userMenuRef}
          onClick={(e) => {
            e.stopPropagation();
            setOpenUserMenu(!openUserMenu);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 12px',
            backgroundColor: '#FDFDFC',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            cursor: 'pointer'
          }}
        >
          <img
            src="Images/profile-icon-img.png"
            alt=""
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#1f2937',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.name}
            </div>
          </div>
          <div style={{
            backgroundColor: '#f3f4f6',
            borderRadius: '6px',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UnfoldMoreIcon style={{ fontSize: '16px', color: '#1f2937' }} />
          </div>
        </div>

        {openUserMenu && createPortal(
          <div
            ref={userMenuDropdownRef}
            data-user-menu-dropdown
            style={{
              position: 'fixed',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: '220px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              zIndex: 99999
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Item
              icon={LogoutOutlinedIcon}
              label={t("Se déconnecter")}
              onClick={() => {
                TokenService.removeUser();
                dispatch(fetchUser(null));
                setOpenUserMenu(false);
              }}
              className="sb-logout"
              style={{ padding: '12px 16px' }}
            />
          </div>,
          document.body
        )}
      </div>
    </aside>
  );
}
